import { z } from 'zod'

/**
 * Who can be named as the cause of a recorded action, in the API's casing.
 * Prisma's `AuditActorType` is the same idea in a different casing and also
 * includes `SIGNER`, which these surfaces never write. Each surface below
 * uses a subset, derived from this list so a new kind has to be placed on
 * purpose.
 */
export const ACTOR_KINDS = ['api_key', 'tenant_user', 'staff', 'system'] as const

export const actorKindSchema = z.enum(ACTOR_KINDS)

export type ActorKind = z.infer<typeof actorKindSchema>

/** Request log: a key, a member, or staff. System rows are not MCP calls. */
export const MCP_ACTOR_KINDS = ['api_key', 'tenant_user', 'staff'] as const satisfies readonly ActorKind[]

export const mcpActorKindSchema = z.enum(MCP_ACTOR_KINDS)

export type McpActorKind = z.infer<typeof mcpActorKindSchema>

/**
 * Billing history: the MCP kinds, plus PixelSign itself (auto-reload, a closure).
 * Not an alias of {@link ACTOR_KINDS}: a new kind is placed on a surface on purpose,
 * and the tuple check fails until this list matches the MCP kinds plus `system`.
 */
export const BILLING_ACTOR_KINDS = ['api_key', 'tenant_user', 'staff', 'system'] as const satisfies readonly [
  ...typeof MCP_ACTOR_KINDS,
  'system',
]

export const billingActorKindSchema = z.enum(BILLING_ACTOR_KINDS)

export type BillingActorKind = z.infer<typeof billingActorKindSchema>

/** Tenant activity as the dashboard shows it: a member, staff, or PixelSign. */
export const ACTIVITY_ACTOR_KINDS = ['tenant_user', 'staff', 'system'] as const satisfies readonly ActorKind[]

export const activityActorKindSchema = z.enum(ACTIVITY_ACTOR_KINDS)

export type ActivityActorKind = z.infer<typeof activityActorKindSchema>
